/**
 * Theme provider — four-state theming for the app.
 *
 * States:
 *   'classic' — **DEFAULT** — "Shopkeeper Classic": light, dense, utilitarian.
 *               Grey canvas, white hairline cards, classic red primary,
 *               dark-navy left sidebar. Sets `data-theme="classic"` on
 *               `<html>` which activates the `[data-theme="classic"]`
 *               token block + shell rules in globals.css.
 *   'premium' — futuristic dark charcoal + electric cyan, glass
 *               surfaces, aurora glow, metallic tiles. Sets
 *               `data-theme="premium"` on `<html>` which activates the
 *               parallel token block in globals.css and all
 *               `[data-theme="premium"] .*` utilities.
 *   'system'  — follow the OS colour-scheme preference (light-or-dark)
 *   'light'   — force the editorial light theme
 *   'dark'    — force the classic dark theme (`<html class="dark">`)
 *
 * Default choice — why Shopkeeper Classic (as of 2026-09-10):
 *   Our first paying client (Krishna Traders — 377 parties, 2,776
 *   invoices) is migrating off the reference desktop billing app and the owner's brief was
 *   "same look, same to same — later will change". The sidebar tree was
 *   already mirrored 1:1 (see LeftNav.tsx, 2026-09-08); this theme
 *   finishes the job visually so day-one users see the light grey
 *   canvas, red accent and dark navy sidebar they have used for years,
 *   instead of a dark cyan UI they have to re-learn. Premium stays
 *   fully available as an explicit choice in User menu → Appearance
 *   (it is still the theme we sell on; it just isn't what a migrating
 *   shopkeeper should be dropped into on first login). As before, only
 *   users who never picked a theme get the new default; every stored
 *   explicit choice — including 'premium' — is honoured unchanged.
 *
 * Previous default choice — why premium (2026-09-07 → 2026-09-10):
 *   After 3 iterations of premium polish, real-user testing on Krishna's
 *   phone (owner of Krishna Traders on the Pesticides Retail plan)
 *   validated the premium theme as the definitive "this is a real
 *   product" experience. Users who prefer light/dark keep their explicit
 *   choice; only users who never picked (a fresh install, or those who
 *   had chosen 'system' — which we no longer default to) get premium on
 *   next visit. The Appearance picker in User menu → Appearance always
 *   lets them change back.
 *
 * Persistence:
 *   User's explicit choice is saved to localStorage under `nids.theme`.
 *   'system' clears the key — meaning the next read falls back to the
 *   NEW default (premium at the time; now classic), which slightly biases
 *   returning "I want to follow my OS" users into the default. Acceptable trade-off given how
 *   few users deliberately picked 'system' before this change; the
 *   Appearance picker restores their preference in one tap.
 *
 * Contract:
 *   • setTheme() writes to <html> in the same tick, so the visual change
 *     is instant (no waiting for React re-render).
 *   • useTheme() returns the current mode + a setter. Components that
 *     only need to know "is premium?" should call `useIsPremium()`.
 *   • The initial write happens in main.tsx BEFORE React renders, to
 *     avoid a flash of light theme on first paint for premium users.
 */
import { useCallback, useEffect, useState } from 'react';

export type ThemeMode = 'system' | 'light' | 'dark' | 'premium' | 'classic';

const LS_KEY = 'nids.theme';

/** The default theme served to anyone who has never made an explicit choice. */
const DEFAULT_THEME: ThemeMode = 'classic';

/**
 * Read the persisted mode. Returns DEFAULT_THEME when nothing is stored or
 * the stored value doesn't validate — never throws.
 *
 * ON THE 2026-09-26 RENAME OF THIS MODE.
 *   'classic' shipped under a different name until 2026-09-26, and that old
 *   string is still sitting in localStorage on every device whose owner had
 *   selected it. It is deliberately NOT in the list below, and there is
 *   deliberately no legacy-value mapping, because the fall-through already
 *   produces the right answer: an unrecognised value returns DEFAULT_THEME,
 *   and DEFAULT_THEME *is* this same theme. Those users get the theme they
 *   chose; the only side effect is that their choice stops being an explicit
 *   one, which is invisible until they open the Appearance picker.
 *
 *   That reasoning rests entirely on `DEFAULT_THEME === 'classic'`. If the
 *   default is ever changed, this stops being a no-op and starts silently
 *   moving those users to a theme they never picked — at which point the
 *   migration has to be done properly: read the raw string once at boot,
 *   rewrite it to 'classic', and never read it again. Do that rather than
 *   re-adding a lookup table, so the old string lives in one migration step
 *   with an expiry date instead of permanently in this module.
 */
export function readStoredTheme(): ThemeMode {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  try {
    const v = window.localStorage.getItem(LS_KEY);
    if (v === 'light' || v === 'dark' || v === 'premium' || v === 'classic' || v === 'system') return v;
  } catch {
    /* localStorage may throw in private mode / iframes */
  }
  return DEFAULT_THEME;
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Apply a mode to <html>. Synchronous — updates the attribute/class in
 * the same tick so the paint is correct on the very next frame.
 *
 * Attribute matrix:
 *   system → resolves to dark|light based on OS pref, then applies that.
 *   light  → class="" data-theme=""
 *   dark   → class="dark" data-theme=""
 *   premium → class="" data-theme="premium"  (premium implies its own
 *             dark scheme via `color-scheme: dark` in globals.css)
 *   classic → class="" data-theme="classic"  (light scheme — the token
 *             block sets `color-scheme: light`; NO `.dark` class, so
 *             every `dark:` utility stays inert exactly like 'light')
 */
export function applyTheme(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  // Resolve 'system' to a concrete mode first.
  const resolved: Exclude<ThemeMode, 'system'> =
    mode === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : mode;

  el.classList.toggle('dark', resolved === 'dark');
  if (resolved === 'premium' || resolved === 'classic') {
    // Attribute-driven themes. Each has its own `[data-theme="…"]` token
    // block in globals.css; light/dark are class/no-class and carry no
    // attribute so their `:root` / `.dark` tokens apply untouched.
    el.setAttribute('data-theme', resolved);
  } else {
    el.removeAttribute('data-theme');
  }
}

/**
 * Called ONCE at app boot (from main.tsx) before React renders. Reads
 * the stored preference and writes to <html>. Prevents the light-flash
 * that would otherwise happen for premium users while React initialises.
 */
export function initTheme(): void {
  applyTheme(readStoredTheme());
}

/**
 * React hook. Returns the current mode plus a setter that persists to
 * localStorage. Subscribes to system-preference changes so 'system' mode
 * re-applies when the OS flips light/dark (useful for scheduled dark
 * mode on iOS/Android).
 */
export function useTheme(): { mode: ThemeMode; setMode: (m: ThemeMode) => void } {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredTheme());

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    applyTheme(next);
    try {
      // Persist EVERY explicit choice including 'system'. Before the
      // default changed from 'system' → 'premium' (2026-09-07), we
      // deleted the key on 'system' because it was already the default.
      // Now that premium is the default, a user who explicitly picks
      // 'system' would fall back to premium on next visit if we cleared
      // it — a silent intent violation. Save every choice explicitly.
      window.localStorage.setItem(LS_KEY, next);
    } catch {
      /* localStorage may throw */
    }
  }, []);

  // Re-apply on system-preference change (only relevant when in system mode).
  useEffect(() => {
    if (mode !== 'system' || typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme('system');
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, [mode]);

  return { mode, setMode };
}

/**
 * Cheap boolean for components that only need to conditionally render
 * premium-only decorations (aurora background, floating orbs, etc.).
 *
 * Strictly `mode === 'premium'`. 'classic' is a light, flat theme and
 * must return false here — it deliberately has none of the glass /
 * aurora / metallic treatments.
 */
export function useIsPremium(): boolean {
  const { mode } = useTheme();
  return mode === 'premium';
}
